import React, { useState } from 'react';
import { useScrollLock } from '@hooks';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2, Eye } from 'lucide-react';
import { Equipment, CreateEquipmentData, UpdateEquipmentData } from '../../../types/equipment';
import { formatCurrency } from '../../../utils/helpers';
import { useCategories } from '@hooks';
import { DataTable, Column } from '@components';
import { IconSelect } from '../../../components/atoms/IconSelect';
import { EquipmentForm } from '@components';
import { UploadImage } from '@components';
import { equipmentService } from '../../../services/equipmentService';

interface EquipmentTabProps {
  equipments: Equipment[];
  searchTerm: string;
  selectedCategory: string;
  onSearchChange: (term: string) => void;
  onCategoryChange: (category: string) => void;
  onDeleteEquipment?: (equipment: Equipment) => void;
  loading?: boolean;
  onRefresh: () => void;
}

export const EquipmentTab: React.FC<EquipmentTabProps> = ({
  equipments,
  searchTerm,
  selectedCategory,
  onSearchChange,
  onCategoryChange,
  onDeleteEquipment,
  loading = false,
  onRefresh
}) => {
  const navigate = useNavigate();
  const [showAddEquipmentModal, setShowAddEquipmentModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<Equipment | null>(null);
  useScrollLock(showAddEquipmentModal || !!editingEquipment);

  // Hook de categorías para mostrar el nombre
  const { categories } = useCategories();

  // Handlers para equipment
  const handleAddEquipment = () => setShowAddEquipmentModal(true);
  const handleEditEquipment = (equipment: Equipment) => setEditingEquipment(equipment);

  const handleSaveEquipment = async (data: CreateEquipmentData | UpdateEquipmentData) => {
    try {
      if ('id' in data) {
        // Actualizar equipo existente
        await equipmentService.manageImages(editingEquipment!, data.images as UploadImage[]);
        await equipmentService.updateEquipment(editingEquipment!.id, {
          name: data.name,
          description: data.description,
          pricePerDay: data.pricePerDay,
          categoryId: data.categoryId,
        });
        setEditingEquipment(null);
      } else {
        // Crear nuevo equipo
        await equipmentService.createEquipment(data);
        setShowAddEquipmentModal(false);
      }
      onRefresh();
    } catch (error) {
      console.error('Error saving equipment:', error);
      // Aquí podrías mostrar un toast/mensaje de error
    }
  };

  const filteredEquipment = equipments.filter(equipment => {
    const matchesSearch = equipment.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || equipment.categoryId === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const equipmentColumns: Column<Equipment>[] = [
    {
      key: 'equipment',
      header: 'Equipo',
      width: 'w-2/5',
      minWidth: 'min-w-[300px]',
      render: (equipment) => (
        <div className="flex items-center">
          <img
            src={equipment?.images[0]?.url || 'https://via.placeholder.com/150'}
            alt={equipment.name}
            className="object-cover w-12 h-12 mr-4 rounded-lg"
          />
          <div className="overflow-hidden">
            <div className="text-sm font-medium text-gray-900">{equipment.name}</div>
            <div className="text-sm text-gray-500 truncate">{equipment.description}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Categoría',
      width: 'w-1/5',
      minWidth: 'min-w-[120px]',
      render: (equipment) => {
        const cat = categories.find((c: { id: string }) => c.id === equipment.categoryId);
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
            {cat ? cat.name : equipment.categoryId}
          </span>
        );
      },
    },
    {
      key: 'pricePerDay',
      header: 'Precio',
      width: 'w-1/5',
      minWidth: 'min-w-[100px]',
      render: (equipment) => `${formatCurrency(equipment.pricePerDay)} / día`,
    },
    {
      key: 'actions',
      header: 'Acciones',
      width: 'w-1/5',
      minWidth: 'min-w-[120px]',
      className: 'sticky right-0 bg-white z-10 min-w-[120px] md:static',
      render: (equipment) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/equipment/${equipment.id}`)}
            className="text-blue-600 cursor-pointer hover:text-blue-900"
            title="Ver"
          >
            <Eye size={16} />
          </button>
          <button
            onClick={() => handleEditEquipment(equipment)}
            className="text-yellow-600 cursor-pointer hover:text-yellow-900"
            title="Editar"
          >
            <Edit size={16} />
          </button>
          {onDeleteEquipment && (
            <button
              onClick={() => onDeleteEquipment(equipment)}
              className="text-red-600 cursor-pointer hover:text-red-900"
              title="Eliminar"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Equipment Controls */}
      <div className="flex flex-col items-start justify-between space-y-4 md:flex-row md:items-center md:space-y-0">
        <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
          <div className="relative">
            <Search className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2" size={18} />
            <input
              type="text"
              placeholder="Buscar equipo..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <IconSelect
            value={selectedCategory}
            onChange={onCategoryChange}
            className="min-w-[200px]"
            options={[
              { value: 'all', label: 'Todas las categorías', icon: 'default' },
              ...categories.map(cat => ({
                value: cat.id,
                label: cat.name,
                icon: cat.icon || 'default',
              }))
            ]}
          />
        </div>

        <button
          onClick={handleAddEquipment}
          className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} className="mr-2" />
          Agregar equipo
        </button>
      </div>

      {/* Equipment Table */}
      <DataTable
        data={filteredEquipment}
        columns={equipmentColumns}
        emptyMessage="No se encontraron equipos"
        loading={loading}
        loadingMessage="Cargando equipos..."
        className="mb-4"
        tableClassName="sm:table-fixed"
      />

      {/* Modales */}
      {showAddEquipmentModal && (
        <EquipmentForm
          onSave={handleSaveEquipment}
          onCancel={() => setShowAddEquipmentModal(false)}
        />
      )}

      {editingEquipment && (
        <EquipmentForm
          equipment={editingEquipment}
          onSave={handleSaveEquipment}
          onCancel={() => setEditingEquipment(null)}
        />
      )}
    </div>
  );
};
